import {
  formatDecimalCell,
  formatInventoryCellText,
} from "@/common/lib/format/inventory-cell";

/** Derived ratio column — rounded for display; quantity columns stay verbatim. */
export const UNREALIZED_PROFIT_PCT_COLUMN = "unrealized_profit_pct";

/**
 * Derived quotient (book value ÷ units) in Holdings by Currency. The engine
 * returns it at a 28-digit division context (`268.22352941176470588235294118`),
 * digits no source carries, so it is bounded like the percentage column while
 * units, values and source prices stay lossless (w0/009).
 */
export const AVERAGE_COST_COLUMN = "average_cost";

/** Fraction digits kept for `average_cost`: enough for crypto unit prices. */
const AVERAGE_COST_MAX_FRACTION_DIGITS = 8;

/** Significant digits a sub-unit average keeps once eight places show fewer. */
const AVERAGE_COST_MIN_SIGNIFICANT_DIGITS = 4;

/**
 * Eight places, or more for an average so small that eight would show fewer
 * than four significant digits: a 0.00000000150 USD average is 0.0000000015,
 * not 0 (w4/1007).
 */
function averageCostFractionDigits(value: string): number {
  const leadingZeros = /^[+-]?0*\.(0*)[1-9]/.exec(value.trim())?.[1].length;
  return leadingZeros === undefined
    ? AVERAGE_COST_MAX_FRACTION_DIGITS
    : Math.max(
        AVERAGE_COST_MAX_FRACTION_DIGITS,
        leadingZeros + AVERAGE_COST_MIN_SIGNIFICANT_DIGITS,
      );
}

/**
 * Round a plain decimal string half-up to at most `maxFraction` digits,
 * exactly (no binary floating point), and drop trailing zeros. Text that is
 * not a plain decimal passes through unchanged.
 */
export function roundDecimalString(value: string, maxFraction: number): string {
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return value;
  const [, sign, whole, fraction = ""] = match;
  if (fraction.length <= maxFraction) {
    const trimmed = fraction.replace(/0+$/, "");
    return `${sign}${whole}${trimmed ? `.${trimmed}` : ""}`;
  }
  let scaled = BigInt(whole + fraction.slice(0, maxFraction));
  if (fraction.charCodeAt(maxFraction) >= "5".charCodeAt(0)) scaled += 1n;
  const digits = scaled.toString().padStart(maxFraction + 1, "0");
  const integer = digits.slice(0, digits.length - maxFraction);
  const kept = digits.slice(digits.length - maxFraction).replace(/0+$/, "");
  const isZero = /^0*$/.test(integer) && !kept;
  return `${isZero ? "" : sign}${integer}${kept ? `.${kept}` : ""}`;
}

/** Re-export for Holdings CSV callers and existing tests. */
export const csvObjectToString = formatInventoryCellText;

export { formatDecimalCell };

/**
 * Converts table data (headers and rows) to CSV format
 * @param headers Array of header names
 * @param rows Array of row arrays (cells can be any type)
 * @returns CSV string
 */
export function tableToCSV(
  headers: string[],
  rows: Array<Array<unknown>>,
): string {
  if (!headers || headers.length === 0 || !rows || rows.length === 0) {
    return "";
  }

  // Create CSV header row
  const headerRow = headers.map((header) => `"${header}"`).join(",");

  // Create CSV data rows — percentage column matches on-screen rounding.
  const dataRows = rows.map((row) =>
    row
      .map((cell, cellIndex) => {
        // Handle null/undefined values
        if (cell === null || cell === undefined) {
          return '""';
        }
        // For objects, convert to string representation
        const value =
          typeof cell === "object"
            ? formatInventoryCellText(cell)
            : formatHoldingsCell(cell, headers[cellIndex]);
        // Escape quotes and wrap in quotes
        const escapedValue = value.replace(/"/g, '""');
        return `"${escapedValue}"`;
      })
      .join(","),
  );

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Round a derived percentage for display (two decimals). Exact zeros stay "0";
 * null/empty stay empty; non-numeric text passes through unchanged.
 */
export function formatPercentageCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return "";
    if (/^-?0(?:\.0+)?$/.test(trimmed)) return "0";
    // Rounded as the decimal the query returned: as a double 2.675 sits just
    // below the half, so it read 2.67 (w4/1033).
    if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
      const [whole, fraction = ""] = roundDecimalString(trimmed, 2).split(".");
      return `${whole}.${fraction.padEnd(2, "0")}`;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return trimmed;
    if (parsed === 0) return "0";
    return parsed.toFixed(2);
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return String(value);
    if (value === 0) return "0";
    return value.toFixed(2);
  }

  return String(value);
}

/**
 * Formats a holdings cell. Quantity/cost/price/value stay lossless; only the
 * unrealized_profit_pct ratio column is rounded for readable comparison.
 */
export function formatHoldingsCell(
  value: unknown,
  columnName?: string,
): string {
  if (columnName === UNREALIZED_PROFIT_PCT_COLUMN) {
    return formatPercentageCell(value);
  }
  if (columnName === AVERAGE_COST_COLUMN && typeof value === "string") {
    return roundDecimalString(value, averageCostFractionDigits(value));
  }
  return formatDecimalCell(value);
}

/**
 * Formats a raw cell value from the holdings API response.
 * Intentionally diverges from common/lib/format/format-number.ts: that util
 * accepts a typed number + renderCommas flag; this one accepts unknown (API cells
 * can be strings, null, or booleans) and has no comma option.
 */
export const formatNumber = (value: unknown, columnName?: string): string =>
  formatHoldingsCell(value, columnName);

/**
 * Determines if an unknown value is considered "empty"
 * Returns true for: null, undefined, "0", "", {}, 0, false
 */
export function isEmpty(value: unknown): boolean {
  // null / undefined carry no value — treat as empty. A zero-balance holding
  // (e.g. a fully-netted IRAUSD lot) comes back with null average_cost/price
  // columns; without this those nulls would keep the otherwise-empty row and
  // surface a commodity the user doesn't actually hold.
  if (value === null || value === undefined) {
    return true;
  }

  // Check for string "0" or empty string
  if (typeof value === "string") {
    return value === "0" || value === "";
  }

  // Check for number 0
  if (typeof value === "number") {
    return value === 0;
  }

  // Check for boolean false
  if (typeof value === "boolean") {
    return value === false;
  }

  // Check for empty object {}
  if (value !== null && typeof value === "object") {
    // Handle arrays - empty arrays are not considered empty by this function
    if (Array.isArray(value)) {
      return false;
    }
    // Check if object has no own enumerable properties
    return Object.keys(value).length === 0;
  }

  // For all other types (functions, symbols, etc.), return false
  return false;
}

export const defaultRowsFilter = (row: unknown[][]): unknown[][] => {
  return row.filter((row) => !row.slice(1).every((o: unknown) => isEmpty(o)));
};

/**
 * Units is column 0 (by-currency / by-cost-currency). A row is a real
 * position when its unit inventory holds a nonzero quantity — even when
 * every cost/quote cell is zero or empty (w4/1064). Sold or
 * remembered-price-only groups carry an empty or all-zero inventory.
 */
export const unitsFirstRowsFilter = (rows: unknown[][]): unknown[][] => {
  return rows.filter((row) => {
    const units = row[0];
    if (isEmpty(units)) return false;
    if (typeof units === "object" && units !== null && !Array.isArray(units)) {
      return Object.values(units).some((quantity) => !isEmpty(quantity));
    }
    return true;
  });
};

export const holdingsRowsFilter = (row: unknown[][]): unknown[][] => {
  return row.filter(
    (row) =>
      !isEmpty(row[1]) && !row.slice(1).every((o: unknown) => isEmpty(o)),
  );
};
